import { useEffect, useMemo, useRef, useState } from 'react';
import Layout from 'src/layout/Layout';
import styles from '../DashboardV2/HrDashboard.module.scss';
import {
  EXTENSION_LISTENER_TYPES,
  ExtensionHandler,
  ExtensionHelper
} from 'src/utils/extension.utils';
import { useDispatch, useSelector } from 'react-redux';
import store, { RootState } from 'src/store';
import {
  refreshCallData,
  setCallId,
  setCampaignId,
  setIsAgentOnline,
  setIsAmeyoErroneous,
  setIsCallConnected,
  setIsFeedbackBtnEnable,
  setIsFeedbackPending,
  setManualLoginCreds,
  setShowMainScreen,
  setSlackNotificationSent
} from 'src/reducers/humanReminderSlice';
import {
  AMEYO_CALL_EVENTS,
  AMEYO_STATUS_CODES,
  AMEYO_STATUS_RANKS,
  ErrorTypes
} from 'src/service/naviExtension.service';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import {
  getAgentStats,
  getCustomerDetails,
  getCustomerDetailsRecalibrate,
  setAgentAvailability,
  setCallStatus,
  setCallStatusDisconnected
} from './dc-97/HumanReminderAction';
import {
  resetHumanReminderCustomerDetails,
  setHumanReminderCustomerDetailsLoading
} from 'src/reducers/commonSlice';
import HrCustomerDetails from './HrCustomerDetails';
import {
  AMEYO_SESSION_EVENTS,
  CLICKSTREAM_EVENT_NAMES,
  HRC_CLICKSTREAM,
  HRC_EVENTS
} from '../../service/clickStream.constant';
import { addClickstreamEvent } from '../../service/clickStreamEventService';
import {
  ameyo_errorenous_message,
  errorenous_break_reason,
  HRC_CALL_DISCONNECT_SOURCE_TYPE
} from 'src/constants/Common.constants';
import { pushToLocalStorage } from '@cp/utils/StorageUtils';
import { noop } from '@utils/common';
import isLitmusExperimentEnabled from '@cp/utils/isLitmusExperimentEnabled';
import { LITMUS_EXPERIMENT_NAMES } from '@cp/constants/litmusExperimentNames';
import {
  AmeyoDiagnosticEventGroup,
  AmeyoDiagnosticEventNames,
  AmeyoDiagnosticsMetricTypes,
  GAUGE_METRIC
} from '@cp/constants/ameyoConstants';
import useCallSdk from '@universal-call-sdk/core/lib/useCallSdk';
import AmeyoAdapter from '@universal-call-sdk/adapter-ameyo/lib/main';
import { GenericObject } from '@cp/src/types/CommonConstans';
import {
  AGENT_AVAILABILITY,
  ameyoErroneousErrorMessage,
  BACKEND_AUDIT_STATUSES,
  HRC_ALERTS_SLACK_CHANNEL,
  PreviousReminderRefId,
  SLACK_NOTIFICATION_INTERVAL,
  spadeCallingCampaignId
} from '../DashboardV2/dc-97/dc97Constant';
import AgentTotalCallsAndTime from '../DashboardV2/AgentTotalCallsAndTime';
import CallWaitingScreen from '../DashboardV2/dc-97/CallWaitingScreen';
import axiosInstance, { ApiKeys, getApiUrl } from '@cp/src/utils/ApiHelper';
import { setAgentOnBreak } from '@universal-call-sdk/adapter-ameyo/lib/api';
import sdkHelper from './utils/sdkHelper';
import logger from '@cp/utils/logger';
import { setIsAvailable } from '@cp/reducers/ameyoSlice';
import useAmeyoDiagnostics from '@cp/src/hooks/useAmeyoDiagnostics';

const HrDashBoardV3 = () => {
  const dispatch = useDispatch();
  const [showCallingPopup, setShowCallingPopup] = useState(false);
  const [isAuthBlocked, setIsAuthBlocked] = useState(false);
  const [isMultipleCampaigns, setIsMultipleCampaigns] = useState(false);
  const slackNotificationToTL = useRef<NodeJS.Timeout>();
  let authError = false;
  const showMainScreen = useSelector((state: RootState) => state.humanReminder.showMainScreen);
  const userData = useSelector((state: RootState) => state.common.userData);
  const humanReminderAgentDetails = useSelector(
    (state: RootState) => state.common.humanReminderAgentDetails
  );
  const reminderRefId = useSelector((state: RootState) => state.humanReminder.reminderRefId);
  const campaignId = useSelector((state: RootState) => state.humanReminder.campaignId);
  const isUserImpersonated = useSelector(
    (store: RootState) => store?.common?.userData.userImpersonated
  );

  const isAgentOnline = useSelector((state: RootState) => state.humanReminder.isAgentOnline);
  const isAmeyoErroneous = useSelector((state: RootState) => state.humanReminder.isAmeyoErronous);
  const ameyoUserId = useSelector((state: RootState) => state.humanReminder.ameyoManualLoginEmail);
  const ameyoPassword = useSelector(
    (state: RootState) => state.humanReminder.ameyoManualLoginEmail
  );
  const userReferenceId = useSelector((state: RootState) => state?.common?.userData?.referenceId);
  const disconnectApiLoading = useSelector(
    (state: RootState) => state?.humanReminder?.disconnectApiLoading
  );
  const callId = useSelector((state: RootState) => state?.humanReminder?.callId);

  const customerDetails = useSelector(
    (state: RootState) => state.common.humanReminderCustomerDetails?.humanReminderResponse
  );

  const authData = useSelector((state: RootState) => state.common.userData);
  const { sendAmeyoDiagnostics } = useAmeyoDiagnostics();

  const configUrl = window?.config?.AMEYO_HRC_URL;
  const adapterOptions = useMemo(
    () =>
      ({
        baseUrl: `${window.location.origin}/ameyoHrc`,
        loginUrl: 'app8.ameyoemerge.in',
        eventListenerUrl: `https://app8.ameyoemerge.in:7443/ameyorestapi/pushes?`,
        userName: authData?.email,
        password: authData?.ameyoKey
      } as any),
    [authData?.email, authData?.ameyoKey]
  );

  const {
    acceptCall: acceptCallNew,
    rejectCall: rejectCallNew,
    registerOnCallConnected,
    registerOnCallIncoming,
    callState,
    registerOnForcedLogoutListener,
    registerOnAgentAvailabilityChange,
    registerOnCallDisconnected,
    unmuteCall: unmuteCallNew,
    muteCall: muteCallNew,
    initialize,
    registerOnAdapterReady,
    getAgentAvailability,
    setAvailable,
    setOnBreak,
    getLatestCallState,
    disposeCall,
    logOut
  } = useCallSdk({
    AdapterClass: AmeyoAdapter as any,
    metricsConfig: {
      metricUrl: '/api/ameyo-stats/publish-diagnostic-data', //this is not needed since we are using custom transporter
      isMetricEnabled: false, // Disabled to avoid Cybertron version compatibility issues - using custom metricTransporter instead
      metricFlowName: 'HRC',
      metricTransporter: (metricData: GenericObject) => {
        const metricApi = getApiUrl(ApiKeys.PUBLISH_METRICS_TO_GRAPHANA);
        if (!isUserImpersonated) {
          axiosInstance.post(
            metricApi,
            {
              campaignId: '0', //we are sending the filler campaign id since its not handled in BE can be removed in future
              ...metricData
            },
            {
              headers: { donotHandleError: true }
            }
          );
        }
      }
    },
    clickStreamConfig: {
      clickStreamTransporter: (payload: GenericObject) => {
        logger({
          msg: 'universal call sdk error in HRC flow',
          extras: {
            error: JSON.stringify(payload),
            flow: 'HRC'
          },
          type: 'info'
        });
        addClickstreamEvent(
          CLICKSTREAM_EVENT_NAMES.LH_UniversalCallSDK_CLICKSTREAM_PROCESSOR,
          payload
        );
      }
    },
    adapterOptions
  });

  const callDisconnectSource = useRef<HRC_CALL_DISCONNECT_SOURCE_TYPE | null>(null);

  const sendSlackNotification = (callId: string) => {
    setAgentAvailability(AGENT_AVAILABILITY.AGENT_IDLE, callId);
    clearInterval(slackNotificationToTL.current);
    dispatch(setSlackNotificationSent(true));
  };

  const getReminderRefId = () => {
    return store.getState()?.humanReminder.reminderRefId;
  };
  const getSlackNotificationStatus = () => {
    return store.getState()?.humanReminder?.slackNotificationSent;
  };

  const reCalibrateCall = async (ameyoData: any) => {
    const isEnabled = await isLitmusExperimentEnabled(
      LITMUS_EXPERIMENT_NAMES.HRC_RECALIBRATE_ENABLE,
      {
        'x-customer-id': userReferenceId
      }
    );
    if (!isEnabled.result) {
      return;
    }
    const customerDetails1 = await getCustomerDetailsRecalibrate(callId || '');
    const currentBackendStatus = customerDetails1?.data?.body?.status;
    if (
      (ameyoData?.callStatus === AMEYO_CALL_EVENTS.DISCONNECTED ||
        ameyoData?.callStatus === AMEYO_CALL_EVENTS.IDLE) &&
      currentBackendStatus === BACKEND_AUDIT_STATUSES.ACTIVE
    ) {
      //call disconnect api again
      logger({
        msg: 'call disconnect api during reclaibrate',
        extras: {
          callId: ameyoData?.callId
        }
      });
      dispatch(
        setCallStatusDisconnected({
          humanReminderReferenceId: reminderRefId || '',
          agentRefId: userData?.referenceId || '',
          campaignId,
          cleanup: () => {
            dispatch(setIsFeedbackBtnEnable(true));
            dispatch(setIsCallConnected(false));
            ExtensionHandler.updateIsPending(true);
            handleSlackNotificationToTL();
          },
          phoneNumber: customerDetails?.customerDetails?.connectedNumber + '',
          lan: customerDetails?.loanDetails?.loanAccountNumber,
          crtObjectId: '',
          callId: ameyoData?.callId
        })
      );
    } else if (
      (ameyoData?.callStatus === AMEYO_CALL_EVENTS.DISCONNECTED ||
        ameyoData?.callStatus === AMEYO_CALL_EVENTS.IDLE) &&
      currentBackendStatus === BACKEND_AUDIT_STATUSES.DISPOSED
    ) {
      //resetting state and go back to main screen
      resetCallData();
    } else if (
      (ameyoData?.callStatus === AMEYO_CALL_EVENTS.DISCONNECTED ||
        ameyoData?.callStatus === AMEYO_CALL_EVENTS.IDLE) &&
      currentBackendStatus === BACKEND_AUDIT_STATUSES.DISCONNECTED
    ) {
      //set feedback button enabled again
      dispatch(setIsFeedbackBtnEnable(true));
    }
  };

  function resetCallData() {
    if (getLatestCallState() !== 'IDLE') {
      disposeCall();
    }
    ExtensionHandler.resetCallStatus();
    ExtensionHandler.updateIsPending(false);
    dispatch(resetHumanReminderCustomerDetails(null));
    dispatch(refreshCallData(null));
    localStorage.removeItem('persist:root');
    clearTimeout(slackNotificationToTL.current);
    dispatch(getAgentStats());
    callDisconnectSource.current = null;
  }

  //Api Calls for Agent and Customer Details
  useEffect(() => {
    dispatch(getAgentStats());
    const refId = getReminderRefId();
    const slackNotificationStatus = getSlackNotificationStatus();
    if (callId) {
      const ameyoData = getLatestCallState();
      reCalibrateCall({
        callId,
        callStatus: ameyoData?.toLowerCase()
      });

      dispatch(setHumanReminderCustomerDetailsLoading(true));
      dispatch(
        getCustomerDetails(callId, () => {
          rejectCallNew();
          resetCallData();
          dispatch(setShowMainScreen(false));
          setShowCallingPopup(false);
        })
      );
    }
    if (ExtensionHandler.currentCallStatus().disconnectedAt && callId && !slackNotificationStatus) {
      const timeDiffAfterDisconnect =
        Date.now() - ExtensionHandler.currentCallStatus().disconnectedAt;
      clearTimeout(slackNotificationToTL.current);
      if (timeDiffAfterDisconnect < SLACK_NOTIFICATION_INTERVAL) {
        slackNotificationToTL.current = setTimeout(() => {
          sendSlackNotification(callId);
        }, SLACK_NOTIFICATION_INTERVAL - timeDiffAfterDisconnect);
      } else {
        sendSlackNotification(callId);
      }
    }
  }, []);

  useEffect(() => {
    if (!userData?.email && !userData?.ameyoKey) {
      return;
    }
    sdkHelper.getAgentAvailability = getAgentAvailability;
    sdkHelper.setAvailable = setAvailable;
    sdkHelper.setOnBreak = setOnBreak;
    sdkHelper.logoOut = logOut;
    authData?.userImpersonated ? noop() : initialize();
    registerOnAdapterReady(() => {
      window.postMessage({ type: 'onAmeyoStatusChange', data: 'ready' });
    });
    registerOnCallIncoming((call: GenericObject) => {});
    registerOnCallConnected((call: GenericObject) => {});
    registerOnCallDisconnected((call: GenericObject) => {
      dispatch(setIsCallConnected(false));
    });
    registerOnAgentAvailabilityChange((isAvailable: boolean, reason: string) => {
      if (reason === errorenous_break_reason) {
        toast(ameyo_errorenous_message, { type: 'error', autoClose: false });
        logger({
          msg: 'ameyo is errorenous',
          extras: {
            flow: 'HRC',
            email: authData.email || ''
          }
        });
      }
      dispatch(setIsAgentOnline(isAvailable));
    });
    registerOnForcedLogoutListener(() => {
      addClickstreamEvent(AMEYO_SESSION_EVENTS.LH_AMEYO_FORCED_LOGOUT);
      dispatch(setIsAvailable(false));
      toast('you have been forced logout from ameyo please reload the page', {
        type: 'error',
        autoClose: false
      });
      logger({
        msg: 'ameyo is forced logout',
        extras: {
          flow: 'HRC',
          email: authData?.email || ''
        },
        type: 'info'
      });
    });
    window.ameyoInitialized = true;
  }, [userData]);

  const onCallAcceptedHandler = () => {
    acceptCallNew();
    setShowCallingPopup(false);
    callDisconnectSource.current = HRC_CALL_DISCONNECT_SOURCE_TYPE.USER;
  };

  const onCallRejectedHandler = () => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_REJECT_BUTTON_CLICKED], {
      currentCallStatus: ExtensionHandler.currentCallStatus(),
      campaignId,
      callId,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });
    rejectCallNew();
    setShowCallingPopup(false);
  };
  function focusAmeyoTab() {
    ExtensionHandler.focusAmeyoTab();
    addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_WINDOW_OPEN, {
      email: ameyoUserId ?? userData?.email,
      password: ameyoPassword ?? userData?.ameyoKey,
      callId,
      campaignId,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });
  }

  const handleSlackNotificationToTL = () => {
    if (!callId) {
      return;
    }
    clearTimeout(slackNotificationToTL.current);
    slackNotificationToTL.current = setTimeout(() => {
      sendSlackNotification(callId);
    }, SLACK_NOTIFICATION_INTERVAL);
  };

  const disConnectHandler = () => {
    callDisconnectSource.current = HRC_CALL_DISCONNECT_SOURCE_TYPE.AGENT;
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.DISCONNECT_BUTTON_CLICKED], {
      currentCallStatus: ExtensionHandler.currentCallStatus(),
      campaignId,
      callId: callId
    });
    rejectCallNew();
    // ExtensionHandler.disconnectCall();
  };

  const onMultipleCampaignsNextButtonClicked = () => {
    setIsMultipleCampaigns(false);
  };
  const ameyoGenericClickstreamCallback = (messageData: any) => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_GENERIC_CLICKSTREAM], {
      campaignId,
      data: messageData,
      reminderRefId: localStorage.getItem(PreviousReminderRefId) || ''
    });
  };

  const onAmeyoErroneous = () => {
    toast(ameyoErroneousErrorMessage, { type: 'error' });
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_AMEYO_ERRONEOUS], {
      campaignId,
      agentReferenceId: userData?.referenceId
    });
    dispatch(setIsAmeyoErroneous(true));
  };

  const ameyoBackgroundTabOpenedCallback = () => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_AMEYO_WINDOW_BACKGROUND_OPEN_SUCCESS], {
      email: ameyoUserId ?? userData?.email,
      callId: callId,
      campaignId,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });
  };

  const ameyoTabOpeningAttemptCallback = (data: any) => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_AMEYO_WINDOW_BACKGROUND_OPEN_ATTEMPT], {
      ...data
    });
  };

  const ameyoBackgroundTabOpenFailedCallback = (data: any) => {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_AMEYO_WINDOW_BACKGROUND_OPEN_FAILURE], {
      ...data
    });
  };

  const extensionToggleMidCallCallback = () => {
    addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_TOGGLE_MID_CALL_CLICK, {
      reminderRefId: getReminderRefId(),
      campaignId,
      aggentReferenceId: userData?.referenceId
    });
  };

  const onAmeyoReloadedCallback = () => {
    addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_RELOADED, {
      campaignId,
      aggentReferenceId: userData?.referenceId
    });
  };

  const onAmeyoDiagnosticEventCallback = (messageType: string) => {
    sendAmeyoDiagnostics({
      metricName: messageType,
      metricType: AmeyoDiagnosticEventNames?.[messageType]
        ?.metricType as AmeyoDiagnosticsMetricTypes,
      value: '',
      group: AmeyoDiagnosticEventGroup.HRC
    });
  };

  useEffect(() => {
    const onCallInComingCallback = (
      phoneNumber: number,
      lan: string,
      crtObjectId: string,
      callId: string
    ) => {
      addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_CALL_RINGING_EVENT], {
        showCallingPopup,
        currentCallStatus: ExtensionHandler.currentCallStatus(),
        lan,
        phoneNumber,
        reminderRefId,
        campaignId,
        ameyoStatus: ExtensionHandler.currentAmeyoStatus()
      });
      if (ExtensionHandler.getIsPending() === null || ExtensionHandler.getIsPending() == 'false') {
        setShowCallingPopup(true);
        dispatch(setCallId(callId));
        addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.POPUP_SHOWN], {
          showCallingPopup,
          currentCallStatus: ExtensionHandler.currentCallStatus(),
          lan,
          phoneNumber,
          reminderRefId,
          campaignId,
          ameyoStatus: ExtensionHandler.currentAmeyoStatus()
        });
      } else {
        rejectCallNew();
        addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_MISSED_CALL_SINCE_AGENT_IS_ON_CALL], {
          currentCallStatus: ExtensionHandler.currentCallStatus(),
          lan,
          phoneNumber,
          reminderRefId,
          campaignId,
          ameyoStatus: ExtensionHandler.currentAmeyoStatus()
        });
        toast('you have missed a call', { type: 'error' });
        logger({
          msg: 'missed call',
          extras: {
            email: authData.email || '',
            flow: 'HRC'
          }
        });
      }
    };
    const onCallConnectedCallback = (
      phoneNumber: number,
      lan: string,
      crtObjectId: string,
      callId: string
    ) => {
      addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.ACCEPT_EVENT_RECEIVED], {
        phoneNumber,
        lan,
        crtObjectId,
        currentCallStatus: ExtensionHandler.currentCallStatus(),
        reminderRefId,
        campaignId,
        ameyoStatus: ExtensionHandler.currentAmeyoStatus()
      });
      dispatch(setHumanReminderCustomerDetailsLoading(true));
      dispatch(setCallId(callId));
      dispatch(
        setCallStatus(
          String(phoneNumber),
          lan,
          crtObjectId,
          ExtensionHandler.currentCallStatus().acceptedAt,
          (_error: any) => {
            rejectCallNew();
            resetCallData();
            dispatch(setShowMainScreen(false));
            setShowCallingPopup(false);
            toast(`Error! Please contact support. We are connecting you to the next call.`, {
              type: 'error',
              autoClose: 6500
            });
          },
          campaignId,
          reminderRefId,
          callId
        )
      );
      dispatch(setShowMainScreen(true));
      dispatch(setIsFeedbackPending(true));
      dispatch(setIsCallConnected(true));
      dispatch(setIsFeedbackBtnEnable(false));
      setShowCallingPopup(false);
      ExtensionHandler.updateIsPending(true);
    };
    const onAvailabilityChangeCallback = (isAvailable: boolean) => {
      dispatch(setIsAgentOnline(isAvailable));
      dispatch(setIsFeedbackBtnEnable(true));
      sendAmeyoDiagnostics({
        metricName: AmeyoDiagnosticEventNames.ameyoAvailabilityStatus.name,
        metricType: AmeyoDiagnosticEventNames.ameyoAvailabilityStatus.metricType,
        value: isAvailable ? GAUGE_METRIC.ON : GAUGE_METRIC.OFF,
        group: AmeyoDiagnosticEventGroup.HRC
      });
    };

    const onErrorCallback = (err: any) => {
      // TODO : if >2 branching is there, make the following as switch case and not if else
      if (err?.type === ErrorTypes.AUTH_ERROR) {
        if (userData?.email && userData?.ameyoKey && userData?.token) setIsAuthBlocked(true);
      } else if (err?.type === ErrorTypes.MULTIPLE_CAMPAIGNS) {
        setIsMultipleCampaigns(true);
      } else if (
        err?.type !== ErrorTypes.HIDDEN &&
        err?.type !== ErrorTypes.DOM_ELEMENT_NOT_FOUND
      ) {
        toast(err?.errorMessage, { type: 'error' });
      }
    };
    const onAmeyoStateChangeCallback = (status: AMEYO_STATUS_CODES) => {
      // if change is there and the status is loggedIn or greater, hide the "manual intervention dialogue"
      if (
        AMEYO_STATUS_RANKS[status]?.occuranceOrder >=
        AMEYO_STATUS_RANKS[AMEYO_STATUS_CODES.LOGGED_IN]?.occuranceOrder
      ) {
        setIsAuthBlocked(false);
      }
      if (
        userData?.email &&
        userData?.ameyoKey &&
        status === AMEYO_STATUS_CODES.LOGGED_OUT &&
        userData?.token
      ) {
        !isUserImpersonated && initialize();
        // ExtensionHandler.login(userData.email, userData.ameyoKey);
      } else if (status === AMEYO_STATUS_CODES.READY) {
        toast('Ameyo is Ready', { type: 'success', autoClose: 5000 });

        if (isAmeyoErroneous) {
          setAvailable();
          dispatch(setIsAmeyoErroneous(false));
        }
      } else if (status === AMEYO_STATUS_CODES.LOGGED_IN) {
        if (authError) {
          addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_LOGIN_MANUAL_SUCCESSFUL, {
            email: ameyoUserId ?? userData?.email,
            reminderRefId,
            campaignId,
            ameyoStatus: ExtensionHandler.currentAmeyoStatus()
          });
        } else {
          addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_LOGIN_AUTO_SUCCESSFUL, {
            email: ameyoUserId ?? userData?.email,
            reminderRefId,
            campaignId,
            ameyoStatus: ExtensionHandler.currentAmeyoStatus()
          });
        }
        authError = false;
      } else if (status === AMEYO_STATUS_CODES.AUTH_ERROR) {
        addClickstreamEvent(HRC_CLICKSTREAM.HRC_AMEYO_LOGIN_FAILURE, {
          email: userData?.email,
          ameyoKey: userData?.ameyoKey,
          reminderRefId,
          campaignId,
          ameyoStatus: ExtensionHandler.currentAmeyoStatus()
        });
        authError = true;
      }
    };

    const manualLoginCallback = (data: any) => {
      dispatch(setManualLoginCreds(data));
    };

    const onCampaignIdRecievedCallback = (campaignId: string) => {
      dispatch(setCampaignId(campaignId));
    };
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.onCallInComing,
      onCallInComingCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.onCallConnected,
      onCallConnectedCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.onAvailabilityChange,
      onAvailabilityChangeCallback
    );
    ExtensionHelper.addExtensionListener(EXTENSION_LISTENER_TYPES.onError, onErrorCallback);
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.onAmeyoStateChange,
      onAmeyoStateChangeCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.campaignId,
      onCampaignIdRecievedCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.multipleCampaignsNextButtonClicked,
      onMultipleCampaignsNextButtonClicked
    );
    // ExtensionHelper.addExtensionListener(
    //   EXTENSION_LISTENER_TYPES.toggleAmeyoStatus,
    //   onToggleAmeyoStatus
    // );
    ExtensionHelper.addExtensionListener(EXTENSION_LISTENER_TYPES.ameyoErroneous, onAmeyoErroneous);
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.ameyoGenericClickstream,
      ameyoGenericClickstreamCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.ameyoBackgroundTabOpened,
      ameyoBackgroundTabOpenedCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.ameyoTabOpeningAttempt,
      ameyoTabOpeningAttemptCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.ameyoBackgroundTabOpenFailed,
      ameyoBackgroundTabOpenFailedCallback
    );
    ExtensionHelper.addExtensionListener(EXTENSION_LISTENER_TYPES.manualLogin, manualLoginCallback);
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.extensionToggleMidCallClickEvent,
      extensionToggleMidCallCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.ameyoReloaded,
      onAmeyoReloadedCallback
    );

    // ExtensionHelper.addExtensionListener(
    //   EXTENSION_LISTENER_TYPES.sendCurrentCallStatusAndRecalibrate,
    //   onSendCurrentCallStatus
    // );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.acceptCallClickFailure,
      onAmeyoDiagnosticEventCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.rejectCallClickFailure,
      onAmeyoDiagnosticEventCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.disconnectCallClickFailure,
      onAmeyoDiagnosticEventCallback
    );
    ExtensionHelper.addExtensionListener(
      EXTENSION_LISTENER_TYPES.disposeCallClickFailure,
      onAmeyoDiagnosticEventCallback
    );
  }, []);

  const onCallDisConnectedCallback = (
    phoneNumber: number,
    lan: string,
    crtObjectId: string,
    callId: string
  ) => {
    const reminderRefId = getReminderRefId();
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.DISCONNECT_EVENT_RECEIVED], {
      phoneNumber,
      lan,
      crtObjectId,
      currentCallStatus: ExtensionHandler.currentCallStatus(),
      campaignId,
      humanReminderReferenceId: reminderRefId,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus(),
      customerRefId: customerDetails?.customerDetails?.customerReferenceId,
      agentRefrenceId: userData?.referenceId,
      source: callDisconnectSource.current
    });

    if (ExtensionHandler.currentCallStatus().acceptedAt) {
      // setTimeout(() => {
      //   if (disconnectApiLoading) {
      //     ExtensionHandler.getCurrentCallStatus();
      //   } else {
      //     return;
      //   }
      // }, HRC_API_TIMEOUT);
      logger({
        msg: 'call disconnect api',
        extras: {
          callId: callId
        }
      });
      dispatch(
        setCallStatusDisconnected({
          humanReminderReferenceId: reminderRefId || '',
          agentRefId: userData?.referenceId || '',
          campaignId,
          cleanup: () => {
            dispatch(setIsFeedbackBtnEnable(true));
            dispatch(setIsCallConnected(false));
            ExtensionHandler.updateIsPending(true);
            handleSlackNotificationToTL();
          },
          phoneNumber: phoneNumber + '',
          lan,
          crtObjectId,
          callId
        })
      );
    } else {
      setShowCallingPopup(false);
    }
  };

  ExtensionHelper.addExtensionListener(
    EXTENSION_LISTENER_TYPES.onCallDisConnected,
    onCallDisConnectedCallback
  );

  useEffect(() => {
    reminderRefId && pushToLocalStorage(PreviousReminderRefId, reminderRefId);
  }, [reminderRefId]);

  return (
    <Layout>
      <div className={styles.agentTodaysData}>
        <AgentTotalCallsAndTime
          totalCallsMade={humanReminderAgentDetails?.totalCalls}
          onlineTime={humanReminderAgentDetails?.onlineTime}
          requestProcessedAt={humanReminderAgentDetails?.requestProcessedAt}
        />
      </div>
      {showMainScreen ? (
        <HrCustomerDetails
          resetCallData={resetCallData}
          disConnectHandler={disConnectHandler}
          getLatestCallState={getLatestCallState}
          callState={callState as GenericObject}
        />
      ) : (
        <CallWaitingScreen
          showCallIncomingPopup={showCallingPopup}
          showAuthBlockedPopup={isAuthBlocked}
          showMultipleCampaignsPopup={isMultipleCampaigns}
          focusAmeyoHandler={focusAmeyoTab}
          onCallAcceptHandler={onCallAcceptedHandler}
          onCallRejectHandler={onCallRejectedHandler}
        />
      )}
    </Layout>
  );
};

export default HrDashBoardV3;
